import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { AttendancePanel } from "./AttendancePanel";
import {
  Plus, MagnifyingGlass, CaretRight, FilePdf, FileXls, Camera, PencilSimple, X, FloppyDisk,
  UserCircle, ChatText, Trash, FileText, Clock, UserList, Warning, Brain, Image as ImageIcon,
  LockKey, Download, Eye, ShieldWarning, Printer
} from "@phosphor-icons/react";

const fmt = (n) => n ? `$${Number(n).toLocaleString("es-MX")}` : "\u2014";

export function HRModule() {
  const { user, token } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("employees");
  const [employees, setEmployees] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [empRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/employee-profiles`, { headers }),
        axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
      ]);
      setEmployees(empRes.data || []);
      setAllUsers(usersRes.data || []);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (selectedId) {
    return <EmployeeDetail employeeId={selectedId} onBack={() => { setSelectedId(null); load(); }} />;
  }

  const filtered = employees.filter(e =>
    !search || (e.name || "").toLowerCase().includes(search.toLowerCase()) || (e.position || "").toLowerCase().includes(search.toLowerCase())
  );

  const handleExportAllPDF = async () => {
    try {
      const res = await axios.get(`${API}/admin/employees-report/pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `empleados_reporte.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("PDF descargado");
    } catch { toast.error("Error al exportar"); }
  };

  const handleExportAllExcel = async () => {
    try {
      const res = await axios.get(`${API}/admin/employees-report/excel`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `empleados_reporte.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Excel descargado");
    } catch { toast.error("Error al exportar"); }
  };

  return (
    <div className="space-y-4" data-testid="hr-module">
      <h1 className="text-2xl font-bold text-slate-900">Recursos Humanos</h1>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        <button onClick={() => setTab("employees")} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${tab === "employees" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`} data-testid="hr-tab-employees">
          <UserList size={16} /> Expedientes
        </button>
        <button onClick={() => setTab("attendance")} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${tab === "attendance" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`} data-testid="hr-tab-attendance">
          <Clock size={16} /> Asistencia
        </button>
      </div>

      {tab === "employees" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar empleado..." className="pl-9 pr-3 py-2 border rounded-lg text-sm w-full" data-testid="hr-search" />
            </div>
            <div className="flex gap-2">
              <button onClick={handleExportAllPDF} className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 flex items-center gap-1" data-testid="emp-export-all-pdf"><FilePdf size={14} /> PDF Todos</button>
              <button onClick={handleExportAllExcel} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 flex items-center gap-1" data-testid="emp-export-all-excel"><FileXls size={14} /> Excel</button>
              {isAdmin && <Button className="btn-primary" size="sm" onClick={() => setSelectedId("new")} data-testid="add-employee-btn"><Plus size={16} className="mr-1" /> Nuevo Empleado</Button>}
            </div>
          </div>
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm" data-testid="hr-employees-table">
              <thead><tr className="bg-slate-50 border-b">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Foto</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Puesto</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Ingreso</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Rol</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Sueldo</th>
                <th className="px-4 py-3"></th>
              </tr></thead>
              <tbody className="divide-y">
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} className="py-10 text-center text-slate-400">Sin empleados. Clic "Nuevo Empleado" para crear.</td></tr>
                ) : filtered.map(e => {
                  const linkedUser = allUsers.find(u => u.name === e.name || u.id === e.linked_user_id);
                  return (
                    <tr key={e.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedId(e.id)} data-testid={`emp-row-${e.id}`}>
                      <td className="px-4 py-2">
                        {e.photo_url ? (
                          <img src={`${API}/admin/employee-files/${e.photo_url}`} alt="" className="w-9 h-9 rounded-full object-cover border" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center"><UserCircle size={20} className="text-slate-400" /></div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-800">{e.name}</td>
                      <td className="px-4 py-3 text-slate-600">{e.position || "\u2014"}</td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-xs">{e.hire_date || "\u2014"}</td>
                      <td className="px-4 py-3 text-center">{linkedUser ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{linkedUser.role}</span> : <span className="text-xs text-slate-400">sin cuenta</span>}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-700">{fmt(e.salary)}</td>
                      <td className="px-4 py-3 text-right"><CaretRight size={16} className="text-slate-400" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "attendance" && <AttendancePanel year={new Date().getFullYear()} month={new Date().getMonth() + 1} />}
    </div>
  );
}

// =================== EMPLOYEE DETAIL (Full Expediente) ===================
function EmployeeDetail({ employeeId, onBack }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isNew = employeeId === "new";
  const [emp, setEmp] = useState(null);
  const [editing, setEditing] = useState(isNew);
  const [form, setForm] = useState({ name: "", position: "", hire_date: "", birth_date: "", bank: "", bank_account: "", salary: "", vacation_days: "", emergency_contact: "", emergency_phone: "", notes: "" });
  const [comments, setComments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [attentionCalls, setAttentionCalls] = useState([]);
  const [callTypes, setCallTypes] = useState([]);
  const [psychExams, setPsychExams] = useState([]);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [newCall, setNewCall] = useState({ type_id: "", description: "", date: new Date().toISOString().slice(0, 10) });
  const [showCallForm, setShowCallForm] = useState(false);
  const [showPassForm, setShowPassForm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [activeSection, setActiveSection] = useState("info");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (isNew) return;
    try {
      const [empRes, commRes, docRes, callsRes, typesRes, psychRes, evidRes] = await Promise.all([
        axios.get(`${API}/admin/employee-profiles/${employeeId}`, { headers }),
        axios.get(`${API}/admin/employee-profiles/${employeeId}/comments`, { headers }),
        axios.get(`${API}/admin/employee-profiles/${employeeId}/documents`, { headers }),
        axios.get(`${API}/admin/employee-profiles/${employeeId}/attention-calls`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/attention-call-types`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/employee-profiles/${employeeId}/psych-exams`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/employee-profiles/${employeeId}/evidence`, { headers }).catch(() => ({ data: [] })),
      ]);
      setEmp(empRes.data);
      setComments(commRes.data || []);
      setDocuments(docRes.data || []);
      setAttentionCalls(callsRes.data || []);
      setCallTypes(typesRes.data || []);
      setPsychExams(psychRes.data || []);
      setEvidenceFiles(evidRes.data || []);
      setForm({
        name: empRes.data.name || "", position: empRes.data.position || "",
        hire_date: empRes.data.hire_date || "", birth_date: empRes.data.birth_date || "",
        bank: empRes.data.bank || "", bank_account: empRes.data.bank_account || "",
        salary: String(empRes.data.salary || ""), vacation_days: String(empRes.data.vacation_days || ""),
        emergency_contact: empRes.data.emergency_contact || "", emergency_phone: empRes.data.emergency_phone || "",
        notes: empRes.data.notes || "",
      });
    } catch { toast.error("Error al cargar"); onBack(); }
    finally { setLoading(false); }
  }, [employeeId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    setSaving(true);
    try {
      const payload = { ...form, salary: parseFloat(form.salary || 0), vacation_days: parseInt(form.vacation_days || 0) };
      if (isNew) {
        await axios.post(`${API}/admin/employee-profiles`, payload, { headers });
        toast.success("Empleado registrado");
        onBack();
      } else {
        await axios.put(`${API}/admin/employee-profiles/${employeeId}`, payload, { headers });
        toast.success("Actualizado");
        setEditing(false);
        fetchData();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Eliminar este empleado? Esta accion no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/admin/employee-profiles/${employeeId}`, { headers });
      toast.success("Empleado eliminado");
      onBack();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API}/admin/employee-profiles/${employeeId}/comments`, { content: newComment }, { headers });
      toast.success("Comentario agregado");
      setNewComment("");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleAddCall = async () => {
    if (!newCall.type_id || !newCall.description.trim()) { toast.error("Tipo y descripcion requeridos"); return; }
    const selectedType = callTypes.find(t => t.id === newCall.type_id);
    try {
      await axios.post(`${API}/admin/employee-profiles/${employeeId}/attention-calls`, {
        ...newCall, type_name: selectedType?.name || ""
      }, { headers });
      toast.success("Llamada de atencion registrada");
      setNewCall({ type_id: "", description: "", date: new Date().toISOString().slice(0, 10) });
      setShowCallForm(false);
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleDeleteCall = async (callId) => {
    if (!window.confirm("Eliminar esta llamada de atencion?")) return;
    try {
      await axios.delete(`${API}/admin/employee-profiles/${employeeId}/attention-calls/${callId}`, { headers });
      toast.success("Eliminado");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleUploadFile = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const endpoint = type === "psych" ? "psych-exams/upload" : type === "evidence" ? "evidence/upload" : "documents/upload";
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/admin/employee-profiles/${employeeId}/${endpoint}`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Archivo subido");
      fetchData();
    } catch (er) { toast.error(er.response?.data?.detail || "Error al subir"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/admin/employee-profiles/${employeeId}/photo`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Foto actualizada");
      fetchData();
    } catch (er) { toast.error(er.response?.data?.detail || "Error al subir foto"); }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 4) { toast.error("Minimo 4 caracteres"); return; }
    try {
      const res = await axios.post(`${API}/admin/employee-profiles/${employeeId}/change-password`, { new_password: newPassword }, { headers });
      toast.success(`Contraseña actualizada para ${res.data.user_email}`);
      setNewPassword("");
      setShowPassForm(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al cambiar contraseña"); }
  };

  const handleExportPDF = async () => {
    try {
      const res = await axios.get(`${API}/admin/employee-profiles/${employeeId}/export/pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `bitacora_${(emp?.name || 'reporte').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Bitacora PDF descargada");
    } catch { toast.error("Error al exportar PDF"); }
  };

  const handlePrint = () => { handleExportPDF(); };

  if (loading) return <div className="text-center py-12 text-slate-400">Cargando expediente...</div>;

  const setField = (f, v) => setForm({ ...form, [f]: v });

  const sections = [
    { id: "info", label: "Informacion", icon: UserCircle },
    { id: "calls", label: `Llamadas (${attentionCalls.length})`, icon: Warning },
    { id: "psych", label: `Psicometricos (${psychExams.length})`, icon: Brain },
    { id: "evidence", label: `Evidencia (${evidenceFiles.length})`, icon: ImageIcon },
    { id: "docs", label: `Documentos (${documents.length})`, icon: FileText },
    { id: "log", label: `Bitacora (${comments.length})`, icon: ChatText },
  ];

  return (
    <div data-testid="employee-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-blue-600 mb-4 hover:underline" data-testid="emp-back">
        <CaretRight size={18} className="rotate-180" /> Volver a lista
      </button>

      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <div className="relative" data-testid="emp-photo-section">
            {emp?.photo_url ? (
              <img src={`${API}/admin/employee-files/${emp.photo_url}`} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-slate-200" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center"><UserCircle size={32} className="text-slate-400" /></div>
            )}
            {!isNew && isAdmin && (
              <label className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700">
                <Camera size={12} className="text-white" />
                <input type="file" accept="image/*" onChange={handleUploadPhoto} className="hidden" />
              </label>
            )}
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{isNew ? "Nuevo Empleado" : (emp?.name || "Empleado")}</h1>
            {emp?.position && <p className="text-sm text-slate-500">{emp.position}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          {!isNew && <Button variant="outline" size="sm" onClick={handlePrint} data-testid="emp-print"><Printer size={16} className="mr-1" /> Imprimir</Button>}
          {!isNew && <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="emp-export-pdf"><FilePdf size={16} className="mr-1" /> Bitacora PDF</Button>}
          {!isNew && isAdmin && <Button variant="outline" size="sm" onClick={() => setShowPassForm(!showPassForm)} data-testid="emp-change-pass"><LockKey size={16} className="mr-1" /> Contraseña</Button>}
          {!isNew && !editing && isAdmin && <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="emp-edit-btn"><PencilSimple size={16} className="mr-1" /> Editar</Button>}
          {!isNew && isAdmin && <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleDelete} data-testid="emp-delete-btn"><Trash size={16} className="mr-1" /> Eliminar</Button>}
        </div>
      </div>

      {/* Password Change */}
      {showPassForm && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-center gap-3" data-testid="emp-pass-form">
          <LockKey size={20} className="text-amber-600" />
          <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Nueva contraseña (min 4 caracteres)" className="max-w-xs h-9 text-sm" data-testid="emp-new-password" />
          <Button size="sm" onClick={handleChangePassword} className="bg-amber-600 hover:bg-amber-700 text-white h-9" data-testid="emp-save-password">Cambiar</Button>
          <Button size="sm" variant="outline" onClick={() => setShowPassForm(false)} className="h-9">Cancelar</Button>
        </div>
      )}

      {/* Section Tabs */}
      {!isNew && (
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg mb-4 overflow-x-auto">
          {sections.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 whitespace-nowrap ${activeSection === s.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid={`emp-section-${s.id}`}>
              <s.icon size={14} /> {s.label}
            </button>
          ))}
        </div>
      )}

      {/* ===== INFO SECTION ===== */}
      {(activeSection === "info" || isNew) && (
        <div className="bg-white rounded-xl border p-5 mb-4 space-y-4" data-testid="emp-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label className="text-xs">Nombre *</Label><Input value={form.name} onChange={e => setField("name", e.target.value)} readOnly={!editing} className="mt-1 text-sm h-9" data-testid="emp-name" /></div>
            <div><Label className="text-xs">Puesto</Label><Input value={form.position} onChange={e => setField("position", e.target.value)} readOnly={!editing} className="mt-1 text-sm h-9" data-testid="emp-position" /></div>
            <div><Label className="text-xs">Fecha Ingreso</Label><Input type="date" value={form.hire_date} onChange={e => setField("hire_date", e.target.value)} readOnly={!editing} className="mt-1 text-sm h-9" data-testid="emp-hire-date" /></div>
            <div><Label className="text-xs">Fecha Nacimiento</Label><Input type="date" value={form.birth_date} onChange={e => setField("birth_date", e.target.value)} readOnly={!editing} className="mt-1 text-sm h-9" data-testid="emp-birth-date" /></div>
            <div><Label className="text-xs">Banco</Label><Input value={form.bank} onChange={e => setField("bank", e.target.value)} readOnly={!editing} className="mt-1 text-sm h-9" data-testid="emp-bank" /></div>
            <div><Label className="text-xs">Cuenta Bancaria</Label><Input value={form.bank_account} onChange={e => setField("bank_account", e.target.value)} readOnly={!editing} className="mt-1 text-sm h-9" data-testid="emp-bank-account" /></div>
            <div><Label className="text-xs">Sueldo ($)</Label><Input type="number" value={form.salary} onChange={e => setField("salary", e.target.value)} readOnly={!editing} className="mt-1 text-sm h-9" data-testid="emp-salary" /></div>
            <div><Label className="text-xs">Dias Vacaciones</Label><Input type="number" value={form.vacation_days} onChange={e => setField("vacation_days", e.target.value)} readOnly={!editing} className="mt-1 text-sm h-9" data-testid="emp-vacation" /></div>
            <div><Label className="text-xs">Contacto Emergencia</Label><Input value={form.emergency_contact} onChange={e => setField("emergency_contact", e.target.value)} readOnly={!editing} className="mt-1 text-sm h-9" data-testid="emp-emergency-contact" /></div>
            <div><Label className="text-xs">Tel. Emergencia</Label><Input value={form.emergency_phone} onChange={e => setField("emergency_phone", e.target.value)} readOnly={!editing} className="mt-1 text-sm h-9" data-testid="emp-emergency-phone" /></div>
          </div>
          {editing && <div><Label className="text-xs">Notas</Label><Textarea value={form.notes} onChange={e => setField("notes", e.target.value)} className="mt-1 min-h-[60px] text-sm" data-testid="emp-notes" /></div>}
          {!editing && form.notes && <div><Label className="text-xs">Notas</Label><p className="text-sm text-slate-700 bg-slate-50 p-3 border rounded mt-1">{form.notes}</p></div>}
          {editing && (
            <div className="flex gap-2 pt-2 border-t">
              {!isNew && <Button variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>}
              <Button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="emp-save"><FloppyDisk size={16} className="mr-1" /> {saving ? "Guardando..." : "Guardar"}</Button>
            </div>
          )}
        </div>
      )}

      {/* ===== ATTENTION CALLS SECTION ===== */}
      {activeSection === "calls" && !isNew && (
        <div className="bg-white rounded-xl border p-5 mb-4" data-testid="emp-calls-section">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2"><ShieldWarning size={18} /> Llamadas de Atencion ({attentionCalls.length})</h3>
            <Button size="sm" onClick={() => setShowCallForm(!showCallForm)} data-testid="emp-add-call"><Plus size={14} className="mr-1" /> Nueva Llamada</Button>
          </div>
          {showCallForm && (
            <div className="bg-slate-50 border rounded-lg p-4 mb-4 space-y-3" data-testid="emp-call-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Tipo *</Label>
                  <Select value={newCall.type_id} onValueChange={v => setNewCall({ ...newCall, type_id: v })}>
                    <SelectTrigger className="mt-1 h-9 text-sm" data-testid="emp-call-type"><SelectValue placeholder="Seleccionar tipo" /></SelectTrigger>
                    <SelectContent>
                      {callTypes.map(ct => (
                        <SelectItem key={ct.id} value={ct.id}>
                          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: ct.color }} />{ct.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Fecha</Label>
                  <Input type="date" value={newCall.date} onChange={e => setNewCall({ ...newCall, date: e.target.value })} className="mt-1 h-9 text-sm" data-testid="emp-call-date" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Descripcion *</Label>
                <Textarea value={newCall.description} onChange={e => setNewCall({ ...newCall, description: e.target.value })} className="mt-1 min-h-[60px] text-sm" placeholder="Detalle de la llamada de atencion..." data-testid="emp-call-desc" />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddCall} data-testid="emp-save-call">Registrar</Button>
                <Button size="sm" variant="outline" onClick={() => setShowCallForm(false)}>Cancelar</Button>
              </div>
            </div>
          )}
          <div className="space-y-2" data-testid="emp-calls-list">
            {attentionCalls.map(ac => {
              const ct = callTypes.find(t => t.id === ac.type_id);
              return (
                <div key={ac.id} className="flex items-start gap-3 p-3 border rounded-lg" data-testid={`emp-call-${ac.id}`}>
                  <div className="w-3 h-3 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: ct?.color || "#6B7280" }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-bold" style={{ color: ct?.color || "#6B7280" }}>{ac.type_name || ct?.name || "Sin tipo"}</span>
                      <span className="text-[10px] text-slate-400 font-mono">{ac.date}</span>
                    </div>
                    <p className="text-sm text-slate-700">{ac.description}</p>
                    <p className="text-[10px] text-slate-400 mt-1">Por: {ac.created_by_name}</p>
                  </div>
                  {isAdmin && <button onClick={() => handleDeleteCall(ac.id)} className="text-red-400 hover:text-red-600 shrink-0"><Trash size={14} /></button>}
                </div>
              );
            })}
            {attentionCalls.length === 0 && <p className="text-center text-sm text-slate-400 py-6">Sin llamadas de atencion registradas</p>}
          </div>
        </div>
      )}

      {/* ===== PSYCH EXAMS SECTION ===== */}
      {activeSection === "psych" && !isNew && (
        <div className="bg-white rounded-xl border p-5 mb-4" data-testid="emp-psych-section">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2"><Brain size={18} /> Examenes Psicometricos ({psychExams.length})</h3>
            <div>
              <input type="file" accept=".pdf" onChange={e => handleUploadFile(e, "psych")} className="hidden" id="psych-upload" />
              <label htmlFor="psych-upload" className="inline-flex items-center gap-1.5 px-3 py-1.5 border text-sm text-slate-700 cursor-pointer hover:bg-slate-50 rounded-lg" data-testid="emp-upload-psych">
                <Plus size={14} /> {uploading ? "Subiendo..." : "Subir PDF"}
              </label>
            </div>
          </div>
          <div className="space-y-2" data-testid="emp-psych-list">
            {psychExams.map(px => (
              <div key={px.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => window.open(`${API}/admin/employee-files/${px.file_id}`, '_blank')} data-testid={`emp-psych-${px.id}`}>
                <Brain size={20} className="text-purple-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{px.filename}</p>
                  <p className="text-[10px] text-slate-400">{px.created_at?.slice(0, 10)} — {px.created_by_name}</p>
                </div>
                <Eye size={16} className="text-slate-400" />
              </div>
            ))}
            {psychExams.length === 0 && <p className="text-center text-sm text-slate-400 py-6">Sin examenes psicometricos</p>}
          </div>
        </div>
      )}

      {/* ===== EVIDENCE SECTION ===== */}
      {activeSection === "evidence" && !isNew && (
        <div className="bg-white rounded-xl border p-5 mb-4" data-testid="emp-evidence-section">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2"><ImageIcon size={18} /> Evidencia Fotografica ({evidenceFiles.length})</h3>
            <div>
              <input type="file" accept="image/*,.pdf" onChange={e => handleUploadFile(e, "evidence")} className="hidden" id="evidence-upload" />
              <label htmlFor="evidence-upload" className="inline-flex items-center gap-1.5 px-3 py-1.5 border text-sm text-slate-700 cursor-pointer hover:bg-slate-50 rounded-lg" data-testid="emp-upload-evidence">
                <Camera size={14} /> {uploading ? "Subiendo..." : "Subir Evidencia"}
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="emp-evidence-list">
            {evidenceFiles.map(ev => (
              <div key={ev.id} className="border rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => window.open(`${API}/admin/employee-files/${ev.file_id}`, '_blank')} data-testid={`emp-evidence-${ev.id}`}>
                {ev.content_type?.startsWith("image/") ? (
                  <img src={`${API}/admin/employee-files/${ev.file_id}`} alt="" className="w-full h-32 object-cover" />
                ) : (
                  <div className="w-full h-32 bg-slate-100 flex items-center justify-center"><FileText size={32} className="text-slate-300" /></div>
                )}
                <div className="p-2">
                  <p className="text-[10px] text-slate-600 truncate">{ev.filename}</p>
                  <p className="text-[9px] text-slate-400">{ev.created_at?.slice(0, 10)}</p>
                </div>
              </div>
            ))}
            {evidenceFiles.length === 0 && <p className="text-center text-sm text-slate-400 py-6 col-span-4">Sin evidencia fotografica</p>}
          </div>
        </div>
      )}

      {/* ===== DOCUMENTS SECTION ===== */}
      {activeSection === "docs" && !isNew && (
        <div className="bg-white rounded-xl border p-5 mb-4" data-testid="emp-documents-section">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2"><FileText size={18} /> Documentos ({documents.length})</h3>
            <div>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xlsx" onChange={e => handleUploadFile(e, "doc")} className="hidden" id="doc-upload" />
              <label htmlFor="doc-upload" className="inline-flex items-center gap-1.5 px-3 py-1.5 border text-sm text-slate-700 cursor-pointer hover:bg-slate-50 rounded-lg" data-testid="emp-upload-doc">
                <Plus size={14} /> {uploading ? "Subiendo..." : "Subir Documento"}
              </label>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="emp-docs-list">
            {documents.map(d => (
              <div key={d.id} className="flex items-center gap-3 p-3 border rounded hover:bg-slate-50 cursor-pointer" onClick={() => window.open(`${API}/admin/employee-files/${d.file_id}`, '_blank')} data-testid={`emp-doc-${d.id}`}>
                <FileText size={20} className="text-blue-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{d.filename}</p>
                  <p className="text-xs text-slate-400">{d.created_at?.slice(0, 10)}</p>
                </div>
                <Download size={16} className="text-slate-400" />
              </div>
            ))}
            {documents.length === 0 && <p className="text-center text-sm text-slate-400 py-6 col-span-2">Sin documentos</p>}
          </div>
        </div>
      )}

      {/* ===== BITACORA / LOG SECTION ===== */}
      {activeSection === "log" && !isNew && (
        <div className="bg-white rounded-xl border p-5" data-testid="emp-log-section">
          <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-4"><ChatText size={18} /> Bitacora de Interacciones ({comments.length})</h3>
          <div className="space-y-2 mb-4">
            <Textarea value={newComment} onChange={e => setNewComment(e.target.value)} className="min-h-[60px] text-sm" placeholder="Agregar nota a la bitacora..." data-testid="emp-new-comment" />
            <Button size="sm" className="btn-primary" onClick={handleAddComment} disabled={!newComment.trim()} data-testid="emp-add-comment"><ChatText size={14} className="mr-1" /> Agregar</Button>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="emp-comments-list">
            {comments.map(c => (
              <div key={c.id} className="border p-3 rounded bg-slate-50">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span className="font-medium text-slate-700">{c.user_name}</span>
                  <span className="font-mono">{c.created_at?.slice(0, 10)}</span>
                </div>
                <p className="text-sm text-slate-700">{c.content}</p>
              </div>
            ))}
            {comments.length === 0 && <p className="text-center text-sm text-slate-400 py-6">Sin entradas en la bitacora</p>}
          </div>
        </div>
      )}
    </div>
  );
}
